import React, { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Upload, Image as ImageIcon, Trash2, Loader2, Check, Building2, Moon, Sun } from "lucide-react";
import { toast } from "sonner";
import { logger } from "@/lib/logger";

const DEFAULT_LOGO_DARK = "/assets/happy-retreats-logo-dark.png";   // dark text → on light bg
const DEFAULT_LOGO_LIGHT = "/assets/happy-retreats-logo.png";       // light/white text → on dark bg

const VARIANTS = [
  {
    key: "logo_path_dark",
    title: "Logo · dark text",
    subtitle: "Used on white / light backgrounds (login form, payslips, kiosk header).",
    fallback: DEFAULT_LOGO_DARK,
    previewBg: "bg-white border border-zinc-200",
    icon: Sun,
    iconCls: "text-amber-500",
    testid: "branding-dark",
  },
  {
    key: "logo_path_light",
    title: "Logo · light text",
    subtitle: "Used on dark backgrounds (login hero panel, dark mode, marketing banners).",
    fallback: DEFAULT_LOGO_LIGHT,
    previewBg: "bg-zinc-950",
    icon: Moon,
    iconCls: "text-zinc-400",
    testid: "branding-light",
  },
];

export default function Branding() {
  const [logos, setLogos] = useState({ logo_path_dark: null, logo_path_light: null });
  const [companyName, setCompanyName] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [savedCompany, setSavedCompany] = useState({ name: "", address: "" });
  const [loading, setLoading] = useState(true);
  const [savingCompany, setSavingCompany] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const fileRefs = { logo_path_dark: useRef(null), logo_path_light: useRef(null) };

  const load = async () => {
    try {
      const { data } = await api.get("/branding");
      setLogos({
        logo_path_dark: data.logo_path_dark || null,
        logo_path_light: data.logo_path_light || null,
      });
      setCompanyName(data.company_name || "");
      setCompanyAddress(data.company_address || "");
      setSavedCompany({ name: data.company_name || "", address: data.company_address || "" });
    } catch (e) {
      logger.error("Failed to load branding", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onPick = (key) => fileRefs[key].current?.click();

  const onFile = async (key, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    setBusyKey(key);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: up } = await api.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const payload = { [key]: up.path };
      // First-time upload of the dark variant also sets the legacy logo_path so
      // older PDF/email paths that still read `logo_path` keep working.
      if (key === "logo_path_dark" && !logos.logo_path_dark) {
        payload.logo_path = up.path;
      }
      await api.put("/branding", payload);
      setLogos((l) => ({ ...l, [key]: up.path }));
      toast.success("Logo uploaded — the app will pick the right one automatically.");
    } catch (err) {
      logger.error("Logo upload failed", err);
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusyKey(null);
    }
  };

  const reset = async (key) => {
    if (!window.confirm(`Reset this logo to the bundled Happy Retreats default?`)) return;
    setBusyKey(key);
    try {
      const payload = { [key]: null };
      if (key === "logo_path_dark") payload.logo_path = null;
      await api.put("/branding", payload);
      setLogos((l) => ({ ...l, [key]: null }));
      toast.success("Reset to default");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setBusyKey(null);
    }
  };

  const saveCompany = async () => {
    setSavingCompany(true);
    try {
      const { data } = await api.put("/branding", {
        company_name: companyName || null,
        company_address: companyAddress || null,
      });
      setSavedCompany({ name: data.company_name, address: data.company_address });
      toast.success("Company details saved. They will appear on payslips.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSavingCompany(false);
    }
  };

  const companyDirty = companyName !== savedCompany.name || companyAddress !== savedCompany.address;

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <span className="label-overline">Setup</span>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Branding</h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-xl">
          Upload two logo variants — one with dark text (for light backgrounds) and one with light text (for dark backgrounds).
          The app picks the right one automatically based on each surface.
        </p>
      </div>

      {/* Company details */}
      <div className="bg-white border border-zinc-200 rounded-md p-6" data-testid="branding-company-card">
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="w-4 h-4 text-zinc-500" />
          <span className="label-overline">Company details</span>
        </div>
        <h3 className="font-display text-xl font-bold tracking-tight">Payslip header</h3>
        <p className="text-xs text-zinc-500 mt-1 mb-4">
          These appear at the top of every generated payslip PDF.
        </p>
        <div className="space-y-3">
          <div>
            <Label htmlFor="company-name">Company name</Label>
            <Input
              id="company-name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="e.g. Stayuntapd Conceptual Retreats LLP"
              data-testid="branding-company-name"
            />
          </div>
          <div>
            <Label htmlFor="company-address">Registered address</Label>
            <Textarea
              id="company-address"
              rows={3}
              value={companyAddress}
              onChange={(e) => setCompanyAddress(e.target.value)}
              placeholder="Full registered address — appears in small print on payslips"
              data-testid="branding-company-address"
            />
          </div>
          <Button
            onClick={saveCompany}
            disabled={!companyDirty || savingCompany}
            className="bg-zinc-950 hover:bg-zinc-800"
            data-testid="branding-save-company"
          >
            {savingCompany ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Save company details
          </Button>
        </div>
      </div>

      {/* Two logo cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {VARIANTS.map((v) => {
          const value = logos[v.key];
          const isCustom = !!value;
          const src = value ? `${API}/files?path=${encodeURIComponent(value)}` : v.fallback;
          const busy = busyKey === v.key;
          const Icon = v.icon;
          return (
            <div key={v.key} className="bg-white border border-zinc-200 rounded-md p-5" data-testid={`${v.testid}-card`}>
              <div className="flex items-start justify-between mb-1">
                <div className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${v.iconCls}`} />
                  <span className="label-overline">{v.title}</span>
                </div>
                {isCustom ? (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                    <Check className="w-3 h-3" /> Custom
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500">
                    <ImageIcon className="w-3 h-3" /> Default
                  </span>
                )}
              </div>
              <p className="text-xs text-zinc-500 mb-4">{v.subtitle}</p>

              {/* Preview on its native background */}
              <div className={`${v.previewBg} rounded-lg p-8 flex items-center justify-center min-h-[180px]`} data-testid={`${v.testid}-preview`}>
                {loading ? (
                  <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
                ) : (
                  <img
                    src={src}
                    alt={`${v.title} preview`}
                    className="max-h-32 w-auto object-contain"
                    onError={(e) => { e.target.src = v.fallback; }}
                    data-testid={`${v.testid}-preview-img`}
                  />
                )}
              </div>

              <input
                ref={fileRefs[v.key]}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onFile(v.key, e)}
                data-testid={`${v.testid}-file-input`}
              />
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  onClick={() => onPick(v.key)}
                  disabled={busy}
                  className="bg-zinc-950 hover:bg-zinc-800"
                  data-testid={`${v.testid}-upload-button`}
                >
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  {isCustom ? "Replace" : "Upload"}
                </Button>
                {isCustom && (
                  <Button
                    variant="outline"
                    onClick={() => reset(v.key)}
                    disabled={busy}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid={`${v.testid}-reset-button`}
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Reset
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <ul className="text-xs text-zinc-500 space-y-1 list-disc pl-4 max-w-2xl">
        <li>Recommended format: PNG or SVG with a <strong>transparent</strong> background.</li>
        <li>Recommended minimum dimensions: 400 × 400 px.</li>
        <li>Each surface (login, kiosk header, payslip header, emails) picks the variant that best matches its background.</li>
      </ul>
    </div>
  );
}
